import Box from '@mui/material/Box';

import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    loadActivationSticks,
    loadOnboardDevices,
    loadOnboardDevicesOnlinePeers,
    loadSystemConfigurations,
    selectActivationSticks,
    selectOnboardDevices,
    selectOnboardDevicesOnlinePeers,
} from '../store/devices.reducer';
import { OnboardDevicesTableGrid } from '../Components/OnboardDevicesTableGrid';
import { Device, DeviceWithSticks, OnboardDeviceConnectionStatus } from '../interfaces';
import { useOnboardDevicesStatusWebSocket } from '../hooks/useOnboardDevicesStatusWebSocket';
import {
    buildInitialOnboardDeviceStatusMap,
    ONBOARD_DEVICE_STATUS_SOURCE_SERVER,
} from '../utils/onboardDeviceOnlineStatus';

export const OnboardDevices = () => {
    const dispatch = useAppDispatch();
    const onboardDevices = useAppSelector(selectOnboardDevices);
    const activationSticks = useAppSelector(selectActivationSticks);
    const peersOnline = useAppSelector(selectOnboardDevicesOnlinePeers);
    const [onlineStatusByDeviceId, setOnlineStatusByDeviceId] = useState<
        Record<string, OnboardDeviceConnectionStatus>
    >({});

    useEffect(() => {
        dispatch(loadOnboardDevices());
        dispatch(loadActivationSticks());
        dispatch(loadSystemConfigurations());
        dispatch(loadOnboardDevicesOnlinePeers());
    }, [dispatch]);

    const onboardDevicesWithSticks: DeviceWithSticks[] | null = useMemo(() => {
        if (onboardDevices && activationSticks) {
            return onboardDevices.map((device: Device) => ({
                ...device,
                activationSticks: activationSticks.filter((stick) => stick.deviceId === device.deviceId),
            }));
        } else {
            return null;
        }
    }, [onboardDevices, activationSticks]);

    useEffect(() => {
        if (!onboardDevices) {
            return;
        }

        setOnlineStatusByDeviceId(buildInitialOnboardDeviceStatusMap(peersOnline));
    }, [onboardDevices, peersOnline]);

    useOnboardDevicesStatusWebSocket({
        enabled: Boolean(onboardDevicesWithSticks),
        onConnected: () => {
            dispatch(loadOnboardDevicesOnlinePeers());
        },
        onStatusUpdate: (payload) => {
            if (payload.serverType && payload.serverType !== ONBOARD_DEVICE_STATUS_SOURCE_SERVER) {
                return;
            }

            setOnlineStatusByDeviceId((previousState) => {
                const nextState = { ...previousState };

                if (payload.status === 'online') {
                    nextState[payload.deviceId] = 'online';
                } else {
                    delete nextState[payload.deviceId];
                }

                return nextState;
            });
        },
    });

    return (
        <Box sx={{ width: '100%', padding: '20px', height: '100vh' }}>
            {onboardDevicesWithSticks && (
                <OnboardDevicesTableGrid
                    devices={onboardDevicesWithSticks}
                    onlineStatusByDeviceId={onlineStatusByDeviceId}
                />
            )}
        </Box>
    );
};
